import { useCMTagList } from "api/api";
import WeatherIcons, { getIconFromCMCode } from "components/basic/WeatherIcons";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import SystemImg from "../SystemDiagram/SystemImg";
import LoadingOverlay from "components/basic/LoadingOverlay";
import Card from "components/cards/Card";
import { hourTimeOption } from "utility/timeOptions";
import { System_Diagram, TimeOption } from "model/Datatypes";
import SleepIcon from "resources/icons/SleepIcon";

const SystemDiagramGraph = ({
  className,
  layout,
  tags,
}: {
  className?: string;
  layout?: "wide" | "tall";
  tags: System_Diagram["tags"];
}) => {
  const { systemDiagramData, loading } = useSystemDiagramData(tags);
  const [time, setTime] = useState<TimeOption>(hourTimeOption);
  const FONT_SIZE = "14px";

  const renderPeakSystem = () => {
    const active =
      !!systemDiagramData.peakSystem?.percentage &&
      systemDiagramData.peakSystem.percentage > 0;
    return (
      <>
        <g
          data-name="Group 1954"
          opacity={active ? "1" : "0.3"}
          transform="translate(120 -620)"
        >
          <g data-name="Group 118" transform="translate(646.507 606)">
            <g
              fill="#fff"
              stroke="rgba(0,0,0,0.2)"
              strokeWidth="1"
              data-name="Rectangle 57"
              transform="translate(.493)"
            >
              <rect width="306" height="82" stroke="none" rx="2"></rect>
              <rect width="305" height="81" x="0.5" y="0.5" fill="none" rx="1.5"></rect>
            </g>
          </g>
          <text
            fill="#797979"
            data-name="Peak Heating System"
            fontFamily="Roboto-Medium, Roboto"
            fontWeight="500"
            transform="translate(650 587)"
            fontSize={FONT_SIZE}
          >
            <tspan x="0" y="13">
              Peak Heating System
            </tspan>
          </text>
          <text
            fill="#797979"
            data-name="Power"
            fontFamily="Roboto-Regular, Roboto"
            transform="translate(732 627)"
            fontSize={FONT_SIZE}
          >
            <tspan x="0" y="13">
              {active ? "Output" : null}
            </tspan>
          </text>
          <text
            fill="#797979"
            data-name="0 kWh"
            fontFamily="Roboto-Bold, Roboto"
            fontWeight="400"
            transform="translate(925 627)"
            textAnchor="end"
            fontSize={FONT_SIZE}
          >
            <tspan className="" x="0" y="13">
              {active ? `${systemDiagramData.peakSystem?.percentage} %` : null}
            </tspan>
          </text>
          <path
            fill="none"
            stroke="#000"
            strokeWidth="1"
            d="M713.342 615.99v62.778"
            data-name="Path 725"
            opacity="0.16"
          ></path>
          <g data-name="Group 122" opacity="0.8" transform="translate(352.779 -147.098)">
            <g fill="none" strokeMiterlimit="10" transform="translate(313 782.23)">
              <path
                stroke="#ff5f45"
                strokeWidth="2"
                d="M0 6.089L6.089 0"
                data-name="Line 79"
                transform="translate(21.422)"
              ></path>
              <path
                stroke="#1c1c1c"
                strokeWidth="2"
                d="M.646 18.526l7.524-7.524 4.176 4.163 9.076-9.076"
                data-name="Path 729"
              ></path>
              <path
                stroke="#ff5f45"
                strokeDasharray="1"
                strokeWidth="1"
                d="M0 0L28.442 0"
                data-name="Line 80"
                transform="translate(0 6.813)"
              ></path>
            </g>
          </g>
        </g>
        <g>
          {!active && (
            <g transform="translate(938, 12) scale(0.12)">
              {/* //   <SleepIcon /> */}
              {/* // </g> */}
              <path
                fill="none"
                width="10"
                id="moonPath"
                height="10"
                stroke="rgba(200,200,200)"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="16"
                d="M216.662 152.65A92.032 92.032 0 01103.35 39.338h.003A92.014 92.014 0 10216.66 152.647z"
              ></path>
            </g>
          )}
        </g>
      </>
    );
  };

  const renderHeating = () => {
    const totalPower =
      (systemDiagramData.heating?.power || 0) + (systemDiagramData.hot_water?.power || 0);
    const totalActive =
      systemDiagramData.heating?.active || systemDiagramData.hot_water?.active;
    const heatingActive = systemDiagramData.heating?.active;

    return (
      <>
        <g
          data-name="Group 1957"
          opacity={heatingActive ? "1" : "0.3"}
          transform="translate(197 -375) scale(0.8)"
        >
          <g
            fill="#fff"
            stroke={heatingActive ? "#ff5f45" : "rgba(0,0,0,0.2)"}
            strokeWidth="1"
            data-name="Rectangle 2464"
            transform="translate(1006 850)"
          >
            <path stroke="none" d="M0 0H306V82H0z"></path>
            <path fill="none" d="M0.5 0.5H305.5V81.5H0.5z"></path>
          </g>
          <text
            fill="#797979"
            fontFamily="Roboto-Medium, Roboto"
            fontWeight="500"
            transform="translate(1009 831)"
            fontSize={FONT_SIZE}
          >
            <tspan x="0" y="13">
              Heating
            </tspan>
          </text>
          <text
            fill="#797979"
            fontFamily="Roboto-Regular, Roboto"
            transform="translate(1091 871)"
            fontSize={FONT_SIZE}
          >
            <tspan x="0" y="13">
              {heatingActive && "Energy"}
            </tspan>
          </text>
          <text
            fill="#797979"
            fontFamily="Roboto-Regular, Roboto"
            transform="translate(1091 898)"
            fontSize={FONT_SIZE}
          >
            <tspan x="0" y="13">
              {heatingActive && "Output temperature"}
            </tspan>
          </text>
          <text
            fill="#797979"
            data-name="0"
            fontFamily="Roboto-Bold, Roboto"
            fontWeight="700"
            transform="translate(1286 871)"
            textAnchor="end"
            fontSize={FONT_SIZE}
          >
            <tspan x="0" y="13">
              {heatingActive &&
                systemDiagramData.heating?.energy &&
                `${systemDiagramData.heating.energy.toFixed(1)} kWh`}
            </tspan>
          </text>
          <text
            fill="#797979"
            fontFamily="Roboto-Bold, Roboto"
            fontWeight="700"
            transform="translate(1286 898)"
            textAnchor="end"
            fontSize={FONT_SIZE}
          >
            <tspan x="0" y="13">
              {heatingActive && systemDiagramData.heating?.output_temp?.toFixed(1) + " °C"}
            </tspan>
          </text>
          <path
            fill="none"
            stroke="#000"
            strokeWidth="1"
            d="M1072.342 859.99v62.778"
            data-name="Path 742"
            opacity="0.16"
          ></path>
          <g
            fill="none"
            stroke="#ff5f45"
            strokeLinecap="round"
            strokeMiterlimit="10"
            strokeWidth="1.722"
            data-name="Group 129"
            opacity="0.8"
            transform="translate(1008.129 859.628)"
          >
            <path
              d="M28.065 23.372a4.41 4.41 0 00-2.19 4.008c0 3.072 2.142 5.4 2.142 8.628a5.594 5.594 0 01-2.118 4.53"
              data-name="Path 734"
            ></path>
            <path
              d="M33.519 23.372a4.41 4.41 0 00-2.19 4.008c0 3.072 2.136 5.4 2.136 8.628a5.6 5.6 0 01-2.112 4.53"
              data-name="Path 735"
            ></path>
            <path
              d="M38.972 23.372a4.411 4.411 0 00-2.189 4.008c0 3.072 2.135 5.4 2.135 8.628a5.6 5.6 0 01-2.111 4.53"
              data-name="Path 736"
            ></path>
          </g>
        </g>
        <text
          fill={totalActive ? "#ff5f45" : "#d8d8d8"}
          data-name="142 kW"
          fontFamily="Roboto-Bold, Roboto"
          fontWeight="700"
          transform="translate(816 259)"
          fontSize={FONT_SIZE}
        >
          <tspan x="28.727" y="13">
            {totalActive && totalPower.toFixed(1) + " kW"}
          </tspan>
        </text>
        <text
          fill={heatingActive ? "#ff5f45" : "#797979"}
          data-name="0 kW"
          fontFamily="Roboto-Bold, Roboto"
          fontWeight="700"
          opacity={heatingActive ? "1" : "0.3"}
          transform="translate(893 320)"
          fontSize={FONT_SIZE}
        >
          <tspan x="42.111" y="13">
            {heatingActive && systemDiagramData.heating?.power?.toFixed(1) + " kW"}
          </tspan>
        </text>
        <path
          fill="none"
          stroke={totalActive ? "#ff5f45" : "#000"}
          strokeDasharray="4 2"
          strokeWidth="1"
          d="M824.003 275h90"
          data-name="Path 1048"
          opacity={totalActive ? "1" : "0.1"}
        ></path>
        <path
          fill="none"
          stroke={heatingActive ? "#ff5f45" : "#000"}
          strokeDasharray="4 2"
          strokeWidth="1"
          d="M911.498 276.2v60.3h90.428"
          data-name="Path 1052"
          opacity={heatingActive ? "1" : "0.1"}
        ></path>
        <g>
          {!heatingActive && (
            <g transform="translate(1136, 324)">
              <SleepIcon />
            </g>
          )}
        </g>
      </>
    );
  };

  const renderHotWater = () => {
    const hotWaterActive = systemDiagramData.hot_water?.active;

    return (
      <>
        <g transform="translate(1, 0)">
          <path
            transform="translate(-1, 0)"
            fill="none"
            stroke={hotWaterActive ? "#ff5f45" : "#000"}
            opacity={hotWaterActive ? "1.0" : "0.2"}
            strokeDasharray="4 2"
            strokeLinejoin="round"
            strokeWidth="1"
            d="M911.498 274.5v-64.3h90.8"
            data-name="Path 1051"
          ></path>
          <text
            fill={hotWaterActive ? "#ff5f45" : "#000"}
            opacity={hotWaterActive ? "1.0" : "0.2"}
            fontFamily="Roboto-Bold, Roboto"
            fontWeight="700"
            transform="translate(900 194)"
            fontSize={FONT_SIZE}
          >
            <tspan x="35.727" y="13">
              {hotWaterActive && `${systemDiagramData.hot_water?.power?.toFixed(1)} kW`}
            </tspan>
          </text>
          <g data-name="Component 1 – 18" transform="translate(1000 161) scale(0.8)">
            <g filter="url(#Rectangle_57-6)" transform="translate(-1086.5 -169.5)">
              <g
                fill="#fff"
                stroke={hotWaterActive ? "#ff5f45" : "#e8e8e8"}
                strokeWidth="1"
                data-name="Rectangle 57"
                transform="translate(1086.5 188.5)"
              >
                <rect width="306" height="82" stroke="none" rx="2"></rect>
                <rect width="305" height="81" x="0.5" y="0.5" fill="none" rx="1.5"></rect>
              </g>
            </g>
            <g opacity={hotWaterActive ? "1.0" : "0.3"}>
              <text
                fill="#797979"
                data-name="Hot Water"
                fontFamily="Roboto-Medium, Roboto"
                fontWeight="500"
                transform="translate(3)"
                fontSize={FONT_SIZE}
              >
                <tspan x="0" y="13">
                  Hot Water
                </tspan>
              </text>
              <text
                fill="#797979"
                data-name="Power"
                fontFamily="Roboto-Regular, Roboto"
                transform="translate(85 40)"
                fontSize={FONT_SIZE}
              >
                <tspan x="0" y="13">
                  {hotWaterActive && "Energy"}
                </tspan>
              </text>
              <text
                fill="#797979"
                fontFamily="Roboto-Regular, Roboto"
                transform="translate(85 67)"
                fontSize={FONT_SIZE}
              >
                <tspan x="0" y="13">
                  {hotWaterActive && "Output temperature"}
                </tspan>
              </text>
              <text
                fill="#797979"
                data-name="245 kWh"
                fontFamily="Roboto-Bold, Roboto"
                fontWeight="700"
                transform="translate(280 40)"
                textAnchor="end"
                fontSize={FONT_SIZE}
              >
                <tspan x="0" y="13">
                  {hotWaterActive && `${systemDiagramData.hot_water?.energy?.toFixed(1)} kWh`}
                </tspan>
              </text>
              <text
                fill="#797979"
                data-name="1000L at 70°C"
                fontFamily="Roboto-Bold, Roboto"
                fontWeight="700"
                transform="translate(280 67)"
                textAnchor="end"
                fontSize={FONT_SIZE}
              >
                <tspan x="0" y="13">
                  {hotWaterActive &&
                    `${systemDiagramData.hot_water?.output_temp?.toFixed(1) || ""}°C`}
                </tspan>
              </text>
              <path
                fill="none"
                stroke="#000"
                strokeWidth="1"
                d="M66.342 28.99v62.778"
                data-name="Path 723"
                opacity="0.16"
              ></path>

              <g data-name="Group 130" transform="translate(3.916 28.516)">
                <path
                  fill="#fff"
                  d="M39.718 29.512a4.934 4.934 0 00-3.528-1.47H22.084v1.734h14.088c.87 0 0 .2.642.816s1.2-.45 1.2.42v2.6h1.74v-2.6c.03-1.32.9-.558-.036-1.5z"
                  data-name="Path 737"
                ></path>
                <path
                  fill="none"
                  stroke="#ff5f45"
                  strokeLinecap="round"
                  strokeMiterlimit="10"
                  strokeWidth="1.734"
                  d="M37.624 36.436a2.094 2.094 0 00-.81 1.4c0 1.122.8 2.082.774 3s-.294.984-.774 1.572"
                  data-name="Path 738"
                ></path>
                <path
                  fill="none"
                  stroke="#ff5f45"
                  strokeLinecap="round"
                  strokeMiterlimit="10"
                  strokeWidth="1.734"
                  d="M40.984 36.436a2.093 2.093 0 00-.81 1.4c0 1.122.8 2.082.78 3s-.3.984-.78 1.572"
                  data-name="Path 739"
                ></path>
                <path
                  fill="#161616"
                  d="M40.072 28.1a4.943 4.943 0 00-3.534-1.47H22.432v1.746h14.094a3.228 3.228 0 013.24 3.216v2.634h1.74v-2.6a4.931 4.931 0 00-1.434-3.526z"
                  data-name="Path 740"
                ></path>
                <path
                  fill="#161616"
                  d="M38.452 34.216v-2.61a1.913 1.913 0 00-.534-1.374 1.945 1.945 0 00-1.38-.57H22.432v1.716h14.106a.206.206 0 01.156.06.192.192 0 01.054.138V34.2z"
                  data-name="Path 741"
                ></path>
                <path
                  fill="#161616"
                  d="M0 0H2.16V1.722H0z"
                  data-name="Rectangle 2463"
                  transform="translate(37.816 32.494)"
                ></path>
                <path
                  fill="none"
                  stroke="#161616"
                  strokeMiterlimit="10"
                  strokeWidth="1.734"
                  d="M0 0L0 17.052"
                  data-name="Line 108"
                  transform="translate(22.27 21.484)"
                ></path>
                <path
                  fill="none"
                  stroke="#161616"
                  strokeMiterlimit="10"
                  strokeWidth="1.734"
                  d="M0 4.164L0 0"
                  data-name="Line 109"
                  transform="translate(31.636 22.642)"
                ></path>
                <path
                  fill="none"
                  stroke="#161616"
                  strokeMiterlimit="10"
                  strokeWidth="1.734"
                  d="M0 0L6.654 0"
                  data-name="Line 110"
                  transform="translate(28.294 22.354)"
                ></path>
              </g>
            </g>
          </g>
          <g>
            {!hotWaterActive && (
              <g transform="translate(1136, 194)">
                <SleepIcon />
              </g>
            )}
          </g>
        </g>
      </>
    );
  };

  const renderCooling = () => {
    const coolingActive = systemDiagramData.cooling?.active;
    return (
      <>
        <g
          data-name="Group 1959"
          transform="translate(-18 -1388)"
          opacity={coolingActive ? "1.0" : "0.2"}
        >
          <text
            fill={coolingActive ? "#4ea6ff" : "#000"}
            data-name="142 kW"
            fontFamily="Roboto-Bold, Roboto"
            fontWeight="700"
            transform="translate(555 1697)"
            fontSize={FONT_SIZE}
          >
            <tspan x="38.727" y="13">
              {coolingActive && `${systemDiagramData.cooling?.power?.toFixed(1) || 0} kW`}
            </tspan>
          </text>
          <g filter="url(#Path_720)" transform="translate(-9.5 1368.5)">
            <path
              fill="none"
              stroke={coolingActive ? "#4ea6ff" : "#000"}
              strokeDasharray="4 2"
              strokeWidth="1"
              d="M564.997 343.5h117"
              data-name="Path 720"
            ></path>
          </g>
        </g>
        <g data-name="Group 1956" transform="translate(-570 -1185) scale(0.8)">
          <g
            data-name="Group 116"
            filter="url(#Rectangle_57-5)"
            transform="translate(1084.507 1844) translate(-259.01 -316.5)"
          >
            <g
              fill="#fff"
              stroke={coolingActive ? "#4ea6ff" : "#e8e8e8"}
              strokeWidth="1"
              data-name="Rectangle 57"
              transform="translate(259.5 316.5)"
            >
              <rect width="306" height="82" stroke="none" rx="2"></rect>
              <rect width="305" height="81" x="0.5" y="0.5" fill="none" rx="1.5"></rect>
            </g>
          </g>
          <g opacity={coolingActive ? "1.0" : "0.3"}>
            <text
              fill="#797979"
              fontFamily="Roboto-Medium, Roboto"
              fontWeight="500"
              transform="translate(1088 1825)"
              fontSize={FONT_SIZE}
            >
              <tspan x="0" y="13">
                Cooling
              </tspan>
            </text>
            <text
              fill="#797979"
              data-name="Power"
              fontFamily="Roboto-Regular, Roboto"
              transform="translate(1170 1865)"
              fontSize={FONT_SIZE}
            >
              <tspan x="0" y="13">
                {coolingActive && "Energy"}
              </tspan>
            </text>
            <text
              fill="#797979"
              data-name="Output"
              fontFamily="Roboto-Regular, Roboto"
              transform="translate(1170 1892)"
              fontSize={FONT_SIZE}
            >
              <tspan x="0" y="13">
                {coolingActive && "Output temperature"}
              </tspan>
            </text>
            <text
              fill="#797979"
              data-name="245 kWh"
              fontFamily="Roboto-Bold, Roboto"
              fontWeight="700"
              transform="translate(1365 1865)"
              textAnchor="end"
              fontSize={FONT_SIZE}
            >
              <tspan x="0" y="13">
                {coolingActive && `${systemDiagramData.cooling?.energy?.toFixed(1) || 0} kWh`}
              </tspan>
            </text>
            <text
              fill="#797979"
              fontFamily="Roboto-Bold, Roboto"
              fontWeight="700"
              transform="translate(1365 1892)"
              textAnchor="end"
              fontSize={FONT_SIZE}
            >
              <tspan x="0" y="13">
                {coolingActive &&
                  `${systemDiagramData.cooling?.output_temp?.toFixed(1) || ""}°C`}
              </tspan>
            </text>
            <path
              fill="none"
              stroke="#000"
              strokeWidth="1"
              d="M1151.342 1853.99v62.778"
              data-name="Path 723"
              opacity="0.1"
            ></path>
            <g data-name="Group 128" transform="translate(1086.654 1854.378)">
              <path
                fill="none"
                stroke="#4ea6ff"
                strokeLinecap="round"
                strokeMiterlimit="10"
                strokeWidth="1.758"
                d="M0 0L0 18.666"
                data-name="Line 93"
                transform="translate(31.842 22.622)"
              ></path>
              <path
                fill="none"
                stroke="#4ea6ff"
                strokeLinecap="round"
                strokeMiterlimit="10"
                strokeWidth="1.32"
                d="M2.748 0L0 2.166"
                data-name="Line 94"
                transform="translate(31.842 23.732)"
              ></path>
              <path
                fill="none"
                stroke="#4ea6ff"
                strokeLinecap="round"
                strokeMiterlimit="10"
                strokeWidth="1.32"
                d="M0 0L2.748 2.166"
                data-name="Line 95"
                transform="translate(29.094 23.732)"
              ></path>
              <path
                fill="none"
                stroke="#4ea6ff"
                strokeLinecap="round"
                strokeMiterlimit="10"
                strokeWidth="1.32"
                d="M0 2.172L2.748 0"
                data-name="Line 96"
                transform="translate(29.094 38.258)"
              ></path>
              <path
                fill="none"
                stroke="#4ea6ff"
                strokeLinecap="round"
                strokeMiterlimit="10"
                strokeWidth="1.32"
                d="M2.748 2.172L0 0"
                data-name="Line 97"
                transform="translate(31.842 38.258)"
              ></path>
              <g filter="url(#Line_98)" transform="translate(-261.15 -326.88)">
                <path
                  fill="none"
                  stroke="#4ea6ff"
                  strokeLinecap="round"
                  strokeMiterlimit="10"
                  strokeWidth="1.758"
                  d="M0 0L16.164 9.336"
                  data-name="Line 98"
                  transform="translate(284.91 354.16)"
                ></path>
              </g>
              <path
                fill="none"
                stroke="#4ea6ff"
                strokeLinecap="round"
                strokeMiterlimit="10"
                strokeWidth="1.32"
                d="M0 0L0.504 3.468"
                data-name="Line 99"
                transform="translate(26.094 25.46)"
              ></path>
              <path
                fill="none"
                stroke="#4ea6ff"
                strokeLinecap="round"
                strokeMiterlimit="10"
                strokeWidth="1.32"
                d="M0 1.296L3.252 0"
                data-name="Line 100"
                transform="translate(23.346 28.928)"
              ></path>
              <path
                fill="none"
                stroke="#4ea6ff"
                strokeLinecap="round"
                strokeMiterlimit="10"
                strokeWidth="1.32"
                d="M0.504 3.462L0 0"
                data-name="Line 101"
                transform="translate(37.302 35.108)"
              ></path>
              <path
                fill="none"
                stroke="#4ea6ff"
                strokeLinecap="round"
                strokeMiterlimit="10"
                strokeWidth="1.32"
                d="M3.252 0L0 1.296"
                data-name="Line 102"
                transform="translate(37.302 33.812)"
              ></path>
              <path
                fill="none"
                stroke="#4ea6ff"
                strokeLinecap="round"
                strokeMiterlimit="10"
                strokeWidth="1.758"
                d="M0 9.336L16.164 0"
                data-name="Line 103"
                transform="translate(23.76 27.41)"
              ></path>
              <path
                fill="none"
                stroke="#4ea6ff"
                strokeLinecap="round"
                strokeMiterlimit="10"
                strokeWidth="1.32"
                d="M0 0L3.252 1.296"
                data-name="Line 104"
                transform="translate(23.346 33.812)"
              ></path>
              <path
                fill="none"
                stroke="#4ea6ff"
                strokeLinecap="round"
                strokeMiterlimit="10"
                strokeWidth="1.32"
                d="M0 3.462L0.504 0"
                data-name="Line 105"
                transform="translate(26.094 35.108)"
              ></path>
              <path
                fill="none"
                stroke="#4ea6ff"
                strokeLinecap="round"
                strokeMiterlimit="10"
                strokeWidth="1.32"
                d="M3.252 1.296L0 0"
                data-name="Line 106"
                transform="translate(37.302 28.928)"
              ></path>
              <path
                fill="none"
                stroke="#4ea6ff"
                strokeLinecap="round"
                strokeMiterlimit="10"
                strokeWidth="1.32"
                d="M0.504 0L0 3.468"
                data-name="Line 107"
                transform="translate(37.302 25.46)"
              ></path>
            </g>
          </g>
        </g>
        <g>
          {!coolingActive && (
            <g transform="translate(435, 309)">
              <SleepIcon />
            </g>
          )}
        </g>
      </>
    );
  };

  const renderElectricityInput = () => {
    const electricityActive = true;
    return (
      <>
        <g data-name="Group 1958" transform="translate(-17.859 -1490)">
          <text
            fill="#2fd2a6"
            data-name="142 kW"
            fontFamily="Roboto-Bold, Roboto"
            fontWeight="700"
            transform="translate(554.859 1697)"
            fontSize={FONT_SIZE}
          >
            <tspan x="38.727" y="13">
              {systemDiagramData.electricity?.power?.toFixed(1) || 0} kW
            </tspan>
          </text>
          <g filter="url(#Path_720-2)" transform="translate(-9.64 1470.5)">
            <path
              fill="none"
              stroke="#2fd2a6"
              strokeDasharray="4 2"
              strokeWidth="1"
              d="M565.136 242.5h116.859"
              data-name="Path 720"
            ></path>
          </g>
        </g>{" "}
        <g data-name="Group 1953" transform="translate(65 -490) scale(0.8)">
          <g
            data-name="Group 116"
            filter="url(#Rectangle_57-3)"
            transform="translate(289.507 850) translate(-259.01 -188.5)"
          >
            <g
              fill="#fff"
              stroke="#2fd2a6"
              strokeWidth="1"
              data-name="Rectangle 57"
              transform="translate(259.5 188.5)"
            >
              <rect width="306" height="82" stroke="none" rx="2"></rect>
              <rect width="305" height="81" x="0.5" y="0.5" fill="none" rx="1.5"></rect>
            </g>
          </g>
          <text
            fill="#797979"
            fontFamily="Roboto-Medium, Roboto"
            fontWeight="500"
            fontSize={FONT_SIZE}
            transform="translate(293 831)"
          >
            <tspan x="0" y="13">
              Electricity
            </tspan>
          </text>
          <text
            fill="#797979"
            data-name="Power"
            fontFamily="Roboto-Regular, Roboto"
            transform="translate(375 871)"
            fontSize={FONT_SIZE}
          >
            <tspan x="0" y="13">
              Energy
            </tspan>
          </text>
          <text
            fill="#797979"
            fontFamily="Roboto-Regular, Roboto"
            transform="translate(375 898)"
            fontSize={FONT_SIZE}
          >
            <tspan x="0" y="13">
              Emissions
            </tspan>
          </text>
          <text
            fill="#797979"
            data-name="245 kWh"
            fontFamily="Roboto-Bold, Roboto"
            fontWeight="700"
            fontSize={FONT_SIZE}
            transform="translate(568 871)"
            textAnchor="end"
          >
            <tspan x="0" y="13">
              {systemDiagramData.electricity?.energy?.toFixed(1) || 0} kWh
            </tspan>
          </text>
          <text
            fill="#797979"
            data-name="96% co2 neutral"
            fontFamily="Roboto-Bold, Roboto"
            fontWeight="700"
            fontSize={FONT_SIZE}
            transform="translate(568 898)"
            textAnchor="end"
          >
            <tspan x="0" y="13">
              100% CO
            </tspan>
            <tspan fontSize="10px" baselineShift="sub">
              2
            </tspan>
            <tspan> neutral</tspan>
          </text>
          <path
            fill="none"
            stroke="#000"
            strokeWidth="1"
            d="M356.342 859.99v62.778"
            data-name="Path 723"
            opacity="0.16"
          ></path>
          <g filter="url(#Path_722)" transform="translate(30.5 661.5)">
            <path
              fill="#2fd2a6"
              d="M12.848 32.01V21.429H9L16.7 7v10.581h3.848z"
              data-name="Path 722"
              transform="translate(278.49 211.5)"
            ></path>
          </g>
        </g>
        <g>
          {!electricityActive && (
            <g transform="translate(435, 209)">
              <SleepIcon />
            </g>
          )}
        </g>
      </>
    );
  };

  const renderSVGDefs = () => {
    return (
      <>
        <defs>
          <filter
            id="Rectangle_57"
            width="245"
            height="251"
            x="643.999"
            y="167"
            filterUnits="userSpaceOnUse"
          >
            <feOffset></feOffset>
            <feGaussianBlur result="blur" stdDeviation="12.5"></feGaussianBlur>
            <feFlood floodOpacity="0.102"></feFlood>
            <feComposite in2="blur" operator="in"></feComposite>
            <feComposite in="SourceGraphic"></feComposite>
          </filter>
          <pattern
            id="pattern"
            width="100%"
            height="100%"
            preserveAspectRatio="xMidYMid slice"
            viewBox="0 0 1000 844"
          >
            {SystemImg}
          </pattern>
          <filter
            id="Path_720"
            width="123"
            height="7"
            x="561.999"
            y="340"
            filterUnits="userSpaceOnUse"
          >
            <feOffset></feOffset>
            <feGaussianBlur result="blur-2" stdDeviation="1"></feGaussianBlur>
            <feFlood floodColor="#4ea6ff" floodOpacity="0.149"></feFlood>
            <feComposite in2="blur-2" operator="in"></feComposite>
            <feComposite in="SourceGraphic"></feComposite>
          </filter>
          <filter
            id="Ellipse_78"
            width="51"
            height="51"
            x="942.499"
            y="269"
            filterUnits="userSpaceOnUse"
          >
            <feOffset></feOffset>
            <feGaussianBlur result="blur-3" stdDeviation="6"></feGaussianBlur>
            <feFlood floodColor="#ff5f45" floodOpacity="0.149"></feFlood>
            <feComposite in2="blur-3" operator="in"></feComposite>
            <feComposite in="SourceGraphic"></feComposite>
          </filter>
          <filter
            id="Path_720-2"
            width="122.859"
            height="7"
            x="562.14"
            y="239"
            filterUnits="userSpaceOnUse"
          >
            <feOffset></feOffset>
            <feGaussianBlur result="blur-4" stdDeviation="1"></feGaussianBlur>
            <feFlood floodColor="#2fd2a6" floodOpacity="0.149"></feFlood>
            <feComposite in2="blur-4" operator="in"></feComposite>
            <feComposite in="SourceGraphic"></feComposite>
          </filter>
          <filter
            id="Rectangle_57-2"
            width="336"
            height="112"
            x="244.499"
            y="173.5"
            filterUnits="userSpaceOnUse"
          >
            <feOffset></feOffset>
            <feGaussianBlur result="blur-5" stdDeviation="5"></feGaussianBlur>
            <feFlood floodColor="#2fd2a6" floodOpacity="0.102"></feFlood>
            <feComposite in2="blur-5" operator="in"></feComposite>
            <feComposite in="SourceGraphic"></feComposite>
          </filter>
          <filter
            id="Rectangle_57-3"
            width="381"
            height="157"
            x="221.999"
            y="151"
            filterUnits="userSpaceOnUse"
          >
            <feOffset></feOffset>
            <feGaussianBlur result="blur-6" stdDeviation="12.5"></feGaussianBlur>
            <feFlood floodOpacity="0.071"></feFlood>
            <feComposite in2="blur-6" operator="in"></feComposite>
            <feComposite in="SourceGraphic"></feComposite>
          </filter>
          <filter
            id="Path_722"
            width="29.543"
            height="43.01"
            x="278.487"
            y="209.495"
            filterUnits="userSpaceOnUse"
          >
            <feOffset></feOffset>
            <feGaussianBlur result="blur-7" stdDeviation="3"></feGaussianBlur>
            <feFlood floodColor="#2fd2a6" floodOpacity="0.161"></feFlood>
            <feComposite in2="blur-7" operator="in"></feComposite>
            <feComposite in="SourceGraphic"></feComposite>
          </filter>
          <filter
            id="Rectangle_57-4"
            width="336"
            height="112"
            x="244.5"
            y="301.5"
            filterUnits="userSpaceOnUse"
          >
            <feOffset></feOffset>
            <feGaussianBlur result="blur-8" stdDeviation="5"></feGaussianBlur>
            <feFlood floodColor="#4ea6ff" floodOpacity="0.102"></feFlood>
            <feComposite in2="blur-8" operator="in"></feComposite>
            <feComposite in="SourceGraphic"></feComposite>
          </filter>
          <filter
            id="Rectangle_57-5"
            width="381"
            height="157"
            x="221.999"
            y="279"
            filterUnits="userSpaceOnUse"
          >
            <feOffset></feOffset>
            <feGaussianBlur result="blur-9" stdDeviation="12.5"></feGaussianBlur>
            <feFlood floodOpacity="0.071"></feFlood>
            <feComposite in2="blur-9" operator="in"></feComposite>
            <feComposite in="SourceGraphic"></feComposite>
          </filter>
          <filter
            id="Line_98"
            width="36.566"
            height="29.738"
            x="274.713"
            y="343.961"
            filterUnits="userSpaceOnUse"
          >
            <feOffset></feOffset>
            <feGaussianBlur result="blur-10" stdDeviation="3"></feGaussianBlur>
            <feFlood floodColor="#2f7bd2" floodOpacity="0.302"></feFlood>
            <feComposite in2="blur-10" operator="in"></feComposite>
            <feComposite in="SourceGraphic"></feComposite>
          </filter>
          <filter
            id="Rectangle_2832"
            width="336"
            height="112"
            x="1071.499"
            y="173.5"
            filterUnits="userSpaceOnUse"
          >
            <feOffset></feOffset>
            <feGaussianBlur result="blur-11" stdDeviation="5"></feGaussianBlur>
            <feFlood floodColor="#ff5f45" floodOpacity="0.102"></feFlood>
            <feComposite in2="blur-11" operator="in"></feComposite>
            <feComposite in="SourceGraphic"></feComposite>
          </filter>
          <filter
            id="Rectangle_57-6"
            width="381"
            height="157"
            x="1048.999"
            y="151"
            filterUnits="userSpaceOnUse"
          >
            <feOffset></feOffset>
            <feGaussianBlur result="blur-12" stdDeviation="12.5"></feGaussianBlur>
            <feFlood floodOpacity="0.071"></feFlood>
            <feComposite in2="blur-12" operator="in"></feComposite>
            <feComposite in="SourceGraphic"></feComposite>
          </filter>
          <filter
            id="Rectangle_57-7"
            width="205"
            height="196"
            x="0"
            y="0"
            filterUnits="userSpaceOnUse"
          >
            <feOffset></feOffset>
            <feGaussianBlur result="blur-13" stdDeviation="12.5"></feGaussianBlur>
            <feFlood floodOpacity="0.102"></feFlood>
            <feComposite in2="blur-13" operator="in"></feComposite>
            <feComposite in="SourceGraphic"></feComposite>
          </filter>
          <filter
            id="Path_1116"
            width="70"
            height="50.102"
            x="68.499"
            y="53.739"
            filterUnits="userSpaceOnUse"
          >
            <feOffset dx="1" dy="2"></feOffset>
            <feGaussianBlur result="blur-14" stdDeviation="3"></feGaussianBlur>
            <feFlood floodOpacity="0.188"></feFlood>
            <feComposite in2="blur-14" operator="in"></feComposite>
            <feComposite in="SourceGraphic"></feComposite>
          </filter>
          <filter
            id="Rectangle_57-8"
            width="381"
            height="157"
            x="574.999"
            y="444"
            filterUnits="userSpaceOnUse"
          >
            <feOffset></feOffset>
            <feGaussianBlur result="blur-15" stdDeviation="12.5"></feGaussianBlur>
            <feFlood floodOpacity="0.071"></feFlood>
            <feComposite in2="blur-15" operator="in"></feComposite>
            <feComposite in="SourceGraphic"></feComposite>
          </filter>
          <filter
            id="Line_90"
            width="21"
            height="43.02"
            x="629.976"
            y="502.981"
            filterUnits="userSpaceOnUse"
          >
            <feOffset dy="3"></feOffset>
            <feGaussianBlur result="blur-16" stdDeviation="3"></feGaussianBlur>
            <feFlood floodColor="#ff5f45"></feFlood>
            <feComposite in2="blur-16" operator="in"></feComposite>
            <feComposite in="SourceGraphic"></feComposite>
          </filter>
          <filter
            id="Line_92"
            width="21"
            height="43.02"
            x="649.976"
            y="502.981"
            filterUnits="userSpaceOnUse"
          >
            <feOffset dy="3"></feOffset>
            <feGaussianBlur result="blur-17" stdDeviation="3"></feGaussianBlur>
            <feFlood floodColor="#ff5f45"></feFlood>
            <feComposite in2="blur-17" operator="in"></feComposite>
            <feComposite in="SourceGraphic"></feComposite>
          </filter>
          <filter
            id="Path_781"
            width="21"
            height="43.02"
            x="639.976"
            y="502.981"
            filterUnits="userSpaceOnUse"
          >
            <feOffset dy="3"></feOffset>
            <feGaussianBlur result="blur-18" stdDeviation="3"></feGaussianBlur>
            <feFlood floodColor="#4ea6ff"></feFlood>
            <feComposite in2="blur-18" operator="in"></feComposite>
            <feComposite in="SourceGraphic"></feComposite>
          </filter>
          {/* <linearGradient id="flow_down" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#4ea6ff" />
            <stop offset="100%" stopColor="#ff5f45" />
          </linearGradient> */}
        </defs>
      </>
    );
  };

  const renderHeatpumpInfo = () => {
    return (
      <g data-name="Group 1952" transform="translate(-75 -478)">
        <g
          data-name="Group 114"
          filter="url(#Rectangle_57)"
          transform="translate(728.85 663) translate(-681.35 -204.5)"
        >
          <g
            fill="#fff"
            stroke="#9b9b9b"
            strokeWidth="1"
            data-name="Rectangle 57"
            transform="translate(681.5 204.5)"
          >
            <rect width="170" height="176" stroke="none" rx="2"></rect>
            <rect width="169" height="175" x="0.5" y="0.5" fill="none" rx="1.5"></rect>
          </g>
        </g>
        {/* <path fill="url(#pattern)" d="M0 0H252V213H0z" transform="translate(681 603)"></path> */}
        <path fill="url(#pattern)" d="M0 0H252V213H0z" transform="translate(681 652)"></path>
        <text
          fill="#797979"
          data-name="Heat Pumps"
          fontFamily="Roboto-Medium, Roboto"
          fontWeight="500"
          fontSize={FONT_SIZE}
          transform="translate(732 643)"
        >
          <tspan x="0" y="13">
            Heat Pump
          </tspan>
        </text>
        <text
          fill="#797979"
          fontFamily="Roboto-Regular, Roboto"
          fontSize="12px"
          transform="translate(753 776)"
        >
          <tspan x="-8" y="6">
            {/* In operation {time.display.toLowerCase()} */}
          </tspan>
        </text>
        <text
          fill="#797979"
          data-name="76%"
          fontSize={FONT_SIZE}
          fontFamily="Roboto-Bold, Roboto"
          fontWeight="700"
          transform="translate(782 776)"
        >
          <tspan x="21" y="35">
            {/* N/A */}
          </tspan>
        </text>
        {/* {tags.heatpump2 && (
          <>
            <text
              fill="#797979"
              fontFamily="Roboto-Regular, Roboto"
              transform="translate(753 803)"
              fontSize={FONT_SIZE}
            >
              <tspan x="0" y="13">
                {tags.heatpump2.name}
              </tspan>
            </text>
            <text
              fill="#797979"
              fontSize={FONT_SIZE}
              data-name="89%"
              fontFamily="Roboto-Bold, Roboto"
              fontWeight="700"
              transform="translate(782 803)"
            >
              <tspan x="71.371" y="13">
                89%
              </tspan>
            </text>
          </>
        )} */}
      </g>
    );
  };

  const renderWeather = () => {
    return (
      <g transform={layout === "tall" ? "translate(515, -60)" : ""}>
        <text
          fill="#797979"
          fontFamily="Roboto-Medium, Roboto"
          fontWeight="500"
          transform="translate(13)"
          fontSize={FONT_SIZE}
        >
          <tspan x="0" y="13">
            Weather
          </tspan>
        </text>
        <g
          data-name="Group 1991"
          filter="url(#Rectangle_57-7)"
          transform="translate(9.508 18) translate(-37.01 -37.5)"
        >
          <g
            fill="#fff"
            stroke="#dbdbdb"
            strokeWidth="1"
            data-name="Rectangle 57"
            transform="translate(37.5 37.5)"
          >
            <rect width="130" height="121" stroke="none" rx="2"></rect>
            <rect width="129" height="120" x="0.5" y="0.5" fill="none" rx="1.5"></rect>
          </g>
        </g>
        <g data-name="Group 1993" transform="translate(-86 -1652)">
          <g data-name="Group 1992" transform="translate(125 1680) scale(0.17)">
            <WeatherIcons
              conditions={getIconFromCMCode(systemDiagramData.current_weather || 0)}
            />
          </g>
          <text
            fill="#656565"
            data-name="21°C"
            fontFamily="Roboto-Bold, Roboto"
            fontSize={FONT_SIZE}
            fontWeight="700"
            transform="translate(161 1764)"
          >
            <tspan x="-19" y="5">
              {systemDiagramData.current_temp?.toFixed(1) || ""}°C
            </tspan>
          </text>
        </g>
      </g>
    );
  };

  const renderStorage = () => {
    const storageData = systemDiagramData.thermalStorage;
    const power =
      storageData?.status === "charging"
        ? storageData.power_charging
        : storageData?.power_discharging;

    const statusColor =
      storageData?.status === "charging"
        ? "#ff5f45"
        : storageData?.status === "discharging"
        ? "#ff5f45"
        : "#c8c8c8";

    const currentStatusDisplay =
      storageData?.status === "charging"
        ? "Storing excess heat"
        : storageData?.status === "discharging"
        ? "Providing heat"
        : "";

    return (
      <>
        <text
          fill={statusColor}
          fontSize={FONT_SIZE}
          data-name="142 kW"
          fontFamily="Roboto-Bold, Roboto"
          fontWeight="700"
          transform="rotate(-90 596 -124) translate(-50, -10)"
        >
          <tspan x="30.727" y="25">
            {power?.toFixed(1) || 0} kW
          </tspan>
        </text>

        <path
          fill="none"
          stroke={statusColor}
          strokeDasharray="4 2"
          strokeWidth="1"
          d="M740.5 360.999v201.844"
          data-name="Path 1069"
        ></path>
        {storageData?.status === "discharging" && (
          <>
            <path
              fill="none"
              stroke={statusColor}
              strokeDasharray="4 2"
              strokeWidth="1"
              strokeLinecap="round"
              d="M740.5 360.999 l6,6"
              data-name="Path 1069"
            ></path>
            <path
              fill="none"
              stroke={statusColor}
              strokeDasharray="4 2"
              strokeWidth="1"
              strokeLinecap="round"
              d="M740.5 360.999 l-6,6"
              data-name="Path 1069"
            ></path>
          </>
        )}
        {storageData?.status === "charging" && (
          <>
            <path
              fill="none"
              stroke={statusColor}
              strokeDasharray="4 2"
              strokeWidth="1"
              strokeLinecap="round"
              d="M740.5 561.5 l6,-6"
              data-name="Path 1069"
            ></path>
            <path
              fill="none"
              stroke={statusColor}
              strokeDasharray="4 2"
              strokeWidth="1"
              strokeLinecap="round"
              d="M740.5 561.5 l-6,-6"
              data-name="Path 1069"
            ></path>
          </>
        )}

        <g data-name="Group 1955" transform="translate(33 -1336)">
          <g
            data-name="Group 119"
            filter="url(#Rectangle_57-8)"
            transform="translate(551.507 1898) translate(-612.01 -481.5)"
          >
            <g
              fill="#fff"
              stroke="#9b9b9b"
              strokeWidth="1"
              data-name="Rectangle 57"
              transform="translate(612.5 481.5)"
            >
              <rect width="306" height="102" stroke="none" rx="2"></rect>
              <rect width="305" height="101" x="0.5" y="0.5" fill="none" rx="1.5"></rect>
            </g>
          </g>
          <text
            fill="#797979"
            data-name="Thermal Storage"
            fontFamily="Roboto-Medium, Roboto"
            fontWeight="500"
            fontSize={FONT_SIZE}
            transform="translate(555 1879)"
          >
            <tspan x="0" y="13">
              Thermal Storage
            </tspan>
          </text>
          <text
            fill="#797979"
            fontFamily="Roboto-Regular, Roboto"
            fontSize={FONT_SIZE}
            transform="translate(644 1915)"
          >
            <tspan x="0" y="13">
              Status
            </tspan>
          </text>
          <text
            fill="#797979"
            data-name="Providing heat"
            fontFamily="Roboto-Bold, Roboto"
            fontWeight="700"
            transform="translate(835 1915)"
            fontSize={FONT_SIZE}
            textAnchor="end"
          >
            <tspan x="0" y="13">
              {currentStatusDisplay}
            </tspan>
          </text>
          <text
            fill="#797979"
            fontFamily="Roboto-Regular, Roboto"
            fontSize={FONT_SIZE}
            transform="translate(644 1940)"
          >
            <tspan x="0" y="13">
              Temperature down
            </tspan>
          </text>
          <text
            fill="#797979"
            data-name="30°C"
            fontFamily="Roboto-Bold, Roboto"
            fontSize={FONT_SIZE}
            fontWeight="700"
            transform="translate(835 1940)"
            textAnchor="end"
          >
            <tspan x="0" y="13">
              {systemDiagramData.thermalStorage?.temp_down?.toFixed(1) || ""}°C
            </tspan>
          </text>
          <text
            fill="#797979"
            fontFamily="Roboto-Regular, Roboto"
            fontSize={FONT_SIZE}
            transform="translate(644 1965)"
          >
            <tspan x="0" y="13">
              Temperature up
            </tspan>
          </text>{" "}
          <text
            fill="#797979"
            data-name="30°C"
            fontSize={FONT_SIZE}
            fontFamily="Roboto-Bold, Roboto"
            fontWeight="700"
            transform="translate(835 1965)"
            textAnchor="end"
          >
            <tspan x="0" y="13">
              {systemDiagramData.thermalStorage?.temp_up?.toFixed(1) || ""}°C
            </tspan>
          </text>
          <path
            fill="none"
            stroke="#000"
            strokeWidth="1"
            d="M628.342 1907.99v81.778"
            data-name="Path 726"
            opacity="0.16"
          ></path>
          <g data-name="Group 121" transform="translate(-83 826)">
            <path
              fill="none"
              stroke="#797979"
              strokeMiterlimit="10"
              strokeWidth="2"
              d="M0 0L35 0"
              data-name="Line 86"
              transform="translate(656 1110.977)"
            ></path>
            <g filter="url(#Line_90)" transform="translate(22.5 598.5)">
              <path
                fill="none"
                stroke="#ff5f45"
                strokeLinecap="round"
                strokeMiterlimit="10"
                strokeWidth="3"
                d="M0 0L0 22.02"
                data-name="Line 90"
                opacity="0.5"
                transform="translate(640.48 510.48)"
              ></path>
            </g>
            <g filter="url(#Line_92)" transform="translate(22.5 598.5)">
              <path
                fill="none"
                stroke="#ff5f45"
                strokeLinecap="round"
                strokeMiterlimit="10"
                strokeWidth="3"
                d="M0 0L0 22.02"
                data-name="Line 92"
                opacity="0.5"
                transform="translate(660.48 510.48)"
              ></path>
            </g>
            <g filter="url(#Path_781)" transform="translate(22.5 598.5)">
              <path
                fill="none"
                stroke="#4ea6ff"
                strokeLinecap="round"
                strokeWidth="3"
                d="M650.48 510.48v22.02"
                data-name="Path 781"
                opacity="0.5"
              ></path>
            </g>
            <path
              fill="none"
              stroke="#797979"
              strokeMiterlimit="10"
              strokeWidth="2"
              d="M0 0L35 0"
              data-name="Line 88"
              opacity="0.5"
              transform="translate(656 1111)"
              style={{ isolation: "isolate" }}
            ></path>
            <path
              fill="#f9fdfc"
              d="M0 0H35V5H0z"
              data-name="Rectangle 2462"
              transform="translate(656 1105)"
            ></path>
          </g>
        </g>
      </>
    );
  };

  return (
    <>
      <Card
        timeOptions={[hourTimeOption]}
        onNewTimeSelected={(newTime) => {
          setTime(newTime);
        }}
        timeSelected={time}
        loading={false}
        flipdropdown={true}
        size="full"
      >
        <div className="font-medium text-gray-600 text-lg absolute top-5 left-6">
          System Diagram
        </div>
        <svg
          className={className ? className : ""}
          xmlns="http://www.w3.org/2000/svg"
          xmlnsXlink="http://www.w3.org/1999/xlink"
          width={"100%"}
          height={"100%"}
          // viewBox={layout === "tall" ? "0 0 1021 631" : "0 0 1429.999 601"}
          viewBox={layout === "tall" ? "0 0 997.735 875" : "0 0 1429.999 601"}
        >
          {renderSVGDefs()}
          <g
            data-name="Group 2031"
            transform={layout === "tall" ? "translate(-273 150)" : "translate(27.499 19.5)"}
          >
            {renderHeatpumpInfo()}
            {renderHeating()}
            {renderHotWater()}
            {renderPeakSystem()}
            {renderElectricityInput()}
            {renderCooling()}
            {renderStorage()}
            {renderWeather()}
          </g>
        </svg>
        {loading && <LoadingOverlay fontSize="5rem" />}
      </Card>
    </>
  );
};

export default SystemDiagramGraph;

const useSystemDiagramData = (tags: System_Diagram["tags"]) => {
  const timeFrom = useMemo(() => moment().subtract(1, "hours").valueOf(), []);
  const timeTo = useMemo(() => moment().valueOf(), []);

  const systemTags = useMemo(
    () =>
      [
        tags.cooling.energy,
        tags.cooling.power,
        tags.cooling.output_temp,
        tags.cooling.output_volume,

        tags.heating.energy,
        tags.heating.power,
        tags.heating.output_temp,
        tags.heating.output_volume,

        tags.hot_water.energy,
        tags.hot_water.power,
        tags.hot_water.output_temp,
        tags.hot_water.output_volume,

        tags.electricity.energy,
        tags.electricity.power,

        tags.current_temp,
        tags.current_weather,

        tags.thermal_storage.power_charging,
        tags.thermal_storage.power_discharging,
        tags.thermal_storage.temp_up,
        tags.thermal_storage.temp_down,

        tags.peak_system.percentage,
      ].filter((tag) => tag.length > 0),
    [tags]
  );

  const [systemDiagramData, setsystemDiagramData] = useState<SystemDiagramData>({});

  const { cmData, loading } = useCMTagList(systemTags, timeFrom, timeTo, "MINUTES", true);

  useEffect(() => {
    // if no data from CoM - set empty
    if (!cmData) {
      setsystemDiagramData({});
      return;
    }

    // helper functions
    const getLatestTagValue = (tag: string, absolute?: boolean) => {
      const tagData = cmData.find((d) => d.name === tag);
      if (tagData) {
        let latestValue = tagData.points[tagData.points.length - 1]?.value;
        if (typeof latestValue === "number") {
          if (absolute) latestValue = Math.abs(latestValue);
          return latestValue;
        }
      }
      return undefined;
    };
    const getAverageTagValue = (tag: string, absolute?: boolean) => {
      const tagData = cmData.find((d) => d.name === tag);

      if (tagData && tagData.points.length > 0) {
        let sum = tagData.points.reduce((prev, cur) => prev + cur.value, 0);
        if (absolute) sum = Math.abs(sum);
        return sum / tagData.points.length;
      }
      return undefined;
    };
    const getTagValueChange = (tag: string, multiplier?: number) => {
      const tagData = cmData.find((d) => d.name === tag);
      if (tagData) {
        const firstValue = tagData.points[0]?.value;
        const latestValue = tagData.points[tagData.points.length - 1]?.value;
        if (typeof latestValue === "number" && typeof firstValue === "number") {
          let difference = latestValue - firstValue;
          if (multiplier) difference = difference * multiplier;
          return difference > 0 ? difference : 0;
        }
      }
      return undefined;
    };

    const current_temp = getLatestTagValue(tags.current_temp);
    const current_weather = getLatestTagValue(tags.current_weather);

    const currentHeatingPower = getAverageTagValue(tags.heating.power, true);
    const heating = {
      energy: getTagValueChange(tags.heating.energy, 1000),
      power: currentHeatingPower,
      output_volume: getAverageTagValue(tags.heating.output_volume),
      output_temp: getAverageTagValue(tags.heating.output_temp),
      active: !!currentHeatingPower && currentHeatingPower > tags.lowerThreshhold,
    };

    const currentHotWater = getAverageTagValue(tags.hot_water.power, true);
    const hot_water = {
      energy: getTagValueChange(tags.hot_water.energy, 1000),
      power: currentHotWater,
      output_volume: getAverageTagValue(tags.hot_water.output_volume),
      output_temp: getAverageTagValue(tags.hot_water.output_temp),
      active: !!currentHotWater && currentHotWater > tags.lowerThreshhold,
    };

    const currentCooling = getAverageTagValue(tags.cooling.power, true);
    const cooling = {
      energy: getTagValueChange(tags.cooling.energy, 1000),
      power: currentCooling,
      output_volume: getAverageTagValue(tags.cooling.output_volume),
      output_temp: getAverageTagValue(tags.cooling.output_temp),
      active: !!currentCooling && currentCooling > tags.lowerThreshhold,
    };

    const currentElectricity = getAverageTagValue(tags.electricity.power);
    const electricity = {
      energy: getTagValueChange(tags.electricity.energy, 1000),
      power: currentElectricity,
    };

    let power_discharging = getAverageTagValue(tags.thermal_storage.power_discharging);
    if (typeof power_discharging === "number" && power_discharging < 0)
      power_discharging = power_discharging * -1;

    const power_charging = getAverageTagValue(tags.thermal_storage.power_charging);

    const isCharging =
      power_charging && (!power_discharging || power_charging > power_discharging);
    const isDischarging =
      power_discharging && (!power_charging || power_discharging > power_charging);

    const thermalStorage: SystemDiagramData["thermalStorage"] = {
      temp_up: getAverageTagValue(tags.thermal_storage.temp_up),
      temp_down: getAverageTagValue(tags.thermal_storage.temp_down),
      power_discharging: power_discharging,
      power_charging: power_charging,
      status: isCharging ? "charging" : isDischarging ? "discharging" : "unknown",
    };

    const peakSystem = {
      percentage: getAverageTagValue(tags.peak_system.percentage),
    };

    setsystemDiagramData((prev) => ({
      ...prev,
      current_temp,
      current_weather,
      heating,
      hot_water,
      cooling,
      electricity,
      thermalStorage,
      peakSystem,
    }));
  }, [cmData, tags]);

  return { systemDiagramData, loading };
};

type SystemDiagramData = {
  current_temp?: number;
  current_weather?: number;
  heating?: {
    energy?: number;
    power?: number;
    output_volume?: number;
    output_temp?: number;
    active?: boolean;
  };
  hot_water?: {
    energy?: number;
    power?: number;
    output_volume?: number;
    output_temp?: number;
    active?: boolean;
  };
  cooling?: {
    energy?: number;
    power?: number;
    output_volume?: number;
    output_temp?: number;
    active?: boolean;
  };
  electricity?: {
    energy?: number;
    power?: number;
  };
  thermalStorage?: {
    temp_up?: number;
    temp_down?: number;
    status?: "charging" | "discharging" | "unknown";
    power_discharging?: number;
    power_charging?: number;
  };
  peakSystem?: {
    percentage?: number;
  };
};
